import { useState } from 'react';

const Hero = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [packageType, setPackageType] = useState('');
  const [urgency, setUrgency] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null);

  // Skjult prisberegning baseret på 7 kr/km + moms
  const calculateHiddenPrice = async (pickup: string, delivery: string, urgency: string) => {
    try {
      // Simuleret afstandsberegning (i virkeligheden ville du bruge Google Maps API)
      // For demo formål bruger vi en simpel beregning
      const baseDistance = Math.random() * 50 + 10; // 10-60 km
      const basePrice = baseDistance * 7; // 7 kr per km
      const vatPrice = basePrice * 1.25; // Plus 25% moms
      
      // Tillæg baseret på hastighed
      let urgencyMultiplier = 1;
      if (urgency === 'ekspres') urgencyMultiplier = 1.5;
      else if (urgency === 'samme-dag') urgencyMultiplier = 1.2;
      
      const finalPrice = Math.round(vatPrice * urgencyMultiplier);
      setCalculatedPrice(finalPrice);
      
      return finalPrice;
    } catch (error) {
      return null;
    }
  };

  const handleQuickBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Beregn skjult pris
      const hiddenPrice = await calculateHiddenPrice(pickupAddress, deliveryAddress, urgency);
      
      const formData = new FormData();
      formData.append('name', name);
      formData.append('phone', phone);
      formData.append('email', email);
      formData.append('pickup_address', pickupAddress);
      formData.append('delivery_address', deliveryAddress);
      formData.append('package_type', packageType);
      formData.append('urgency', urgency);
      // Tilføj skjult pris til intern brug (vises ikke til kunde)
      if (hiddenPrice) {
        formData.append('calculated_price_internal', hiddenPrice.toString());
      }

      const response = await fetch('https://readdy.ai/api/form/d466shvjsic9gsa4u0eg', {
        method: 'POST',
        body: new URLSearchParams(formData as any)
      });

      if (response.ok) {
        setSubmitMessage('Tak! Vi kontakter dig inden for 5 minutter med pris og afhentning.');
        setName('');
        setPhone('');
        setEmail('');
        setPickupAddress('');
        setDeliveryAddress('');
        setPackageType('');
        setUrgency('');
        setCalculatedPrice(null);
      } else {
        setSubmitMessage('Der opstod en fejl. Prøv igen eller ring til os.');
      }
    } catch (error) {
      setSubmitMessage('Der opstod en fejl. Prøv igen eller ring til os.');
    }
    
    setIsSubmitting(false);
  };

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/9f0156d3c45edfd4d59e4cf7b146e917.jpg')`
      }}
    >
      <div className="container mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-white">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Hurtig & Pålidelig
              <span className="block text-teal-400">Kurér Service</span>
            </h1>
            <p className="text-xl mb-8 text-gray-200 leading-relaxed">
              Fra Århus til hele Danmark. Professionel levering med fokus på hastighed og pålidelighed. 
              Over 10 års erfaring med kvalitetsservice.
            </p>
            
            <div className="flex flex-wrap gap-6 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                  <i className="ri-speed-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold">Hurtig levering</div>
                  <div className="text-sm text-gray-300">Ekspres og samme-dag</div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                  <i className="ri-award-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold">10 års erfaring</div>
                  <div className="text-sm text-gray-300">Professionel faglighed</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="font-semibold">Pålidelig service</div>
                  <div className="text-sm text-gray-300">Garanteret levering</div>
                </div>
              </div>
            </div>

            <div className="bg-teal-600/20 backdrop-blur-sm rounded-lg p-4 border border-teal-400/30">
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-time-line text-teal-400 text-xl"></i>
                <span className="font-semibold text-lg">Kvalitetsgaranti</span>
              </div>
              <p className="text-sm text-gray-200">
                Vi garanterer hurtig og pålidelig levering med professionel håndtering
              </p>
            </div>
          </div>

          {/* Right - Quick Booking Form */}
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Book Din Levering</h2>
              <p className="text-gray-600">Hurtig booking - vi kontakter dig med alle detaljer!</p>
            </div>

            <form onSubmit={handleQuickBooking} data-readdy-form id="hurtig-booking" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-user-line mr-2"></i>Navn
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="Dit fulde navn"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="ri-phone-line mr-2"></i>Telefon
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="+45 12 34 56 78"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-mail-line mr-2"></i>Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="din@email.dk"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-map-pin-line mr-2"></i>Afhentningsadresse
                </label>
                <input
                  type="text"
                  name="pickup_address"
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Indtast afhentningsadresse"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-map-pin-2-line mr-2"></i>Leveringsadresse
                </label>
                <input
                  type="text"
                  name="delivery_address"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="Indtast leveringsadresse"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-package-line mr-2"></i>Pakketype
                </label>
                <select
                  name="package_type"
                  value={packageType}
                  onChange={(e) => setPackageType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
                  required
                >
                  <option value="">Vælg pakketype</option>
                  <option value="dokument">Dokument/Brev</option>
                  <option value="lille-pakke">Lille pakke (op til 5kg)</option>
                  <option value="mellem-pakke">Mellem pakke (5-20kg)</option>
                  <option value="stor-pakke">Stor pakke (20kg+)</option>
                  <option value="special">Special transport</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <i className="ri-speed-line mr-2"></i>Hastighed
                </label>
                <select
                  name="urgency"
                  value={urgency}
                  onChange={(e) => setUrgency(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
                  required
                >
                  <option value="">Vælg hastighed</option>
                  <option value="ekspres">Ekspres</option>
                  <option value="samme-dag">Samme dag</option>
                  <option value="inden-48-timer">Inden for 48 timer</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-4 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sender...
                  </>
                ) : (
                  <>
                    <i className="ri-calendar-check-line"></i>
                    Book Nu - Få Tilbud
                  </>
                )}
              </button>

              {submitMessage && (
                <div className={`p-4 rounded-lg text-center ${
                  submitMessage.includes('Tak') 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}
            </form>

            <div className="mt-6 text-center">
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="tel:+4521563552"
                  className="bg-teal-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-colors inline-flex items-center justify-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Ring Nu - 24/7
                </a>
              </div>
              <p className="text-xs text-gray-400 mt-1">Baseret i Århus - Servicerer hele Danmark</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
